import React, { useState } from "react";
import { Box, Typography, useTheme, Button } from "@mui/material";

import DecompressThumbnail from "../../assets/videos/decompress_thumbnail.png";
import PatientThumbnail from "../../assets/videos/patient_thumbnail.png";
import ShockwaveThumbnail from "../../assets/videos/shockwave_thumbnail.png";
import LaserThumbnail from "../../assets/videos/laser_thumbnail.png";
import NeuropathyThumbnail from "../../assets/videos/neuropathy_thumbnail.png";

const VideosPage = () => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(null); // Track which video is being hovered

  // Reusable function for handling video clicks
  const handleVideoClick = (title, videoUrl) => {
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { margin: 0; background: black; }
            video { width: 100vw; height: 100vh; object-fit: contain; }
          </style>
        </head>
        <body>
          <video controls controlsList="nodownload" oncontextmenu="return false;">
            <source src="${videoUrl}" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const handlePatientClicked = () => {
    handleVideoClick("Intro to Chiropractic Care", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/NewPatientVideo.mp4");
  };

  const handleDecompressClicked = () => {
    handleVideoClick("Intro to Spinal Decompression Therapy", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Spinal-Decompression-Therapy.mp4");
  };

  const handleShockwaveClicked = () => {
    handleVideoClick("Intro to Shockwave Therapy", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Shockwave Therapy.mp4");
  };

  const handleLaserClicked = () => {
    handleVideoClick("Intro to Class 4 Laser", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Class 4 Laser.mp4");
  };

  const handleNeuropathyClicked = () => {
    handleVideoClick("Intro to Peripheral Neuropathy", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Peripheral Neuropathy.mp4");
  };

  // Spanish video handlers
  const handlePatientSpanishClicked = () => {
    handleVideoClick("Intro to Chiropractic Care - Spanish", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Chiropractic Care-Spanish.mp4");
  };

  const handleDecompressSpanishClicked = () => {
    handleVideoClick("Intro to Spinal Decompression Therapy - Spanish", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Spinal Decompression Therapy-Spanish.mp4");
  };

  const handleShockwaveSpanishClicked = () => {
    handleVideoClick("Intro to Shockwave Therapy - Spanish", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Shockwave Therapy-Spanish.mp4");
  };

  const handleLaserSpanishClicked = () => {
    handleVideoClick("Intro to Class 4 Laser - Spanish", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Class4 Laser-Spanish.mp4");
  };

  const handleNeuropathySpanishClicked = () => {
    handleVideoClick("Intro to Peripheral Neuropathy - Spanish", "https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Peripheral Neuropathy-Spanish.mp4");
  };

  return (
    <Box
      sx={{
        padding: { xs: 4, sm: 10 },
      }}
    >
      <Box sx={{ mb: 6 }}>
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "24px" },
            fontFamily: "Poppins",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: "center",
          }}
        >
          Please choose a video to begin.
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "24px" },
            fontFamily: "Poppins",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            textAlign: "center",
          }}
        >
          Select from our educational video library to learn about different treatment options.
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        sx={{ gap: "20px" }}
        flexDirection="column"
      >
        {/* First Row */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* New Patient Video */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("patient")}
            onMouseLeave={() => setHovered(null)}
            onClick={handlePatientClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "patient" ? "none" : `url(${PatientThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "patient" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/NewPatientVideo.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Chiropractic Care
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handlePatientClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Chiropractic Care
            </Button>
          </Box>

          {/* Spinal Decompression Video */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("decompress")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleDecompressClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm:
                    hovered === "decompress"
                      ? "none"
                      : `url(${DecompressThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "decompress" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Spinal-Decompression-Therapy.mp4"
                  autoPlay
                  onContextMenu={(e) => e.preventDefault()}
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Spinal Decompression Therapy
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleDecompressClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Spinal Decompression Therapy
            </Button>
          </Box>
        </Box>

        {/* Second Row */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* Shockwave Therapy Video */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("shockwave")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleShockwaveClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "shockwave" ? "none" : `url(${ShockwaveThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "shockwave" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Shockwave Therapy.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Shockwave Therapy
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleShockwaveClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Shockwave Therapy
            </Button>
          </Box>

          {/* Class 4 Laser Video */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("laser")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleLaserClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "laser" ? "none" : `url(${LaserThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "laser" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Class 4 Laser.mp4"
                  autoPlay
                  onContextMenu={(e) => e.preventDefault()}
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Class 4 Laser
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleLaserClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
              }}
            >
              Intro to Class 4 Laser
            </Button>
          </Box>
        </Box>

        {/* Third Row */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* Peripheral Neuropathy Video */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("neuropathy")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleNeuropathyClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "neuropathy" ? "none" : `url(${NeuropathyThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "neuropathy" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Peripheral Neuropathy.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Peripheral Neuropathy
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleNeuropathyClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Peripheral Neuropathy
            </Button>
          </Box>

          {/* Empty space for symmetry */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              display: { xs: "none", sm: "block" },
            }}
          />
        </Box>

        {/* Spanish Videos Section */}
        <Box sx={{ mt: 8, mb: 4 }}>
          <Typography
            sx={{
              fontSize: { xs: "20px", md: "28px" },
              fontFamily: "Poppins",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textAlign: "center",
            }}
          >
            Videos en Español
          </Typography>
        </Box>

        {/* Fourth Row - Spanish Videos Part 1 */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* Chiropractic Care Spanish */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("patient-spanish")}
            onMouseLeave={() => setHovered(null)}
            onClick={handlePatientSpanishClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "patient-spanish" ? "none" : `url(${PatientThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "patient-spanish" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Chiropractic Care-Spanish.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Chiropractic Care - Spanish
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handlePatientSpanishClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Chiropractic Care - Spanish
            </Button>
          </Box>

          {/* Spinal Decompression Spanish */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("decompress-spanish")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleDecompressSpanishClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm:
                    hovered === "decompress-spanish"
                      ? "none"
                      : `url(${DecompressThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "decompress-spanish" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Spinal Decompression Therapy-Spanish.mp4"
                  autoPlay
                  onContextMenu={(e) => e.preventDefault()}
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Spinal Decompression Therapy - Spanish
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleDecompressSpanishClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Spinal Decompression Therapy - Spanish
            </Button>
          </Box>
        </Box>

        {/* Fifth Row - Spanish Videos Part 2 */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* Shockwave Therapy Spanish */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("shockwave-spanish")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleShockwaveSpanishClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "shockwave-spanish" ? "none" : `url(${ShockwaveThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "shockwave-spanish" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Shockwave Therapy-Spanish.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Shockwave Therapy - Spanish
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleShockwaveSpanishClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Shockwave Therapy - Spanish
            </Button>
          </Box>

          {/* Class 4 Laser Spanish */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("laser-spanish")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleLaserSpanishClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "laser-spanish" ? "none" : `url(${LaserThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "laser-spanish" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Class4 Laser-Spanish.mp4"
                  autoPlay
                  onContextMenu={(e) => e.preventDefault()}
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Class 4 Laser - Spanish
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleLaserSpanishClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Class 4 Laser - Spanish
            </Button>
          </Box>
        </Box>

        {/* Sixth Row - Spanish Videos Part 3 */}
        <Box
          display="flex"
          justifyContent="center"
          sx={{ gap: "20px" }}
          flexDirection={{ xs: "column", lg: "row" }}
        >
          {/* Peripheral Neuropathy Spanish */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              alignItems: "center",
              textAlign: "center",
              display: { xs: "none", sm: "block" },
            }}
            onMouseEnter={() => setHovered("neuropathy-spanish")}
            onMouseLeave={() => setHovered(null)}
            onClick={handleNeuropathySpanishClicked}
          >
            <Box
              height="300px"
              sx={{
                backgroundImage: {
                  xs: "none",
                  sm: hovered === "neuropathy-spanish" ? "none" : `url(${NeuropathyThumbnail})`,
                },
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "1px solid #007FFF",
                borderRadius: "10px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                mb: 2,
              }}
            >
              {hovered === "neuropathy-spanish" && (
                <video
                  src="https://d2nut6gc5qzbiv.cloudfront.net/34481519/globalvideos/Intro to Peripheral Neuropathy-Spanish.mp4"
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  muted
                  loop
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "20px", md: "28px" },
                fontFamily: "Poppins",
                fontWeight: 500,
                color: theme.palette.text.secondary,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              Intro to Peripheral Neuropathy - Spanish
            </Typography>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Button
              variant="contained"
              onClick={handleNeuropathySpanishClicked}
              sx={{
                width: "100%",
                fontSize: "17px",
                px: 3,
                py: "14px",
                mb: 2,
              }}
            >
              Intro to Peripheral Neuropathy - Spanish
            </Button>
          </Box>

          {/* Empty space for symmetry */}
          <Box
            width={{ xs: "100%", lg: "50%" }}
            maxWidth="700px"
            sx={{
              display: { xs: "none", sm: "block" },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default VideosPage;
